import React, { useCallback } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { SEOText, MetaTags, StaticMetaTags, FAQ } from '@corefront/seo-tools';
import classNames from 'classnames';
import RenderPixel from '../RenderPixel';
import SEOWrapperBreadcrumbs from './SEOWrapperBreadcrumbs';
import CanonicalLink from '../../../components/SEOWrapper/CanonicalLink/CanonicalLink';
import MetaRobots from '../../../components/SEOWrapper/MetaRobots/MetaRobots';
import { withSEOContent } from '../../hocs/withSEOContent';
import {
  useMetaTagsRequestUrl,
  useStaticMetaTagsRequestUrl,
} from '../../../components/SEOWrapper/hooks/useMetaTagsRequestUrl';
import { useSEOTextRequestUrl } from '../../../components/SEOWrapper/hooks/useSEOTextRequestUrl';
import { useFAQRequestUrl } from '../../../components/SEOWrapper/hooks/useFAQRequestUrl';
import { useIsSSRMode } from '../../../hooks/useIsSSRMode';
import { useTranslationMetaTags } from '../../../components/SEOWrapper/hooks/useTranslationMetaTags';
import { useSEOTextFormat } from '../../../components/SEOWrapper/hooks/useSEOTextFormat';
import { useMetaTagsFormat } from '../../../components/SEOWrapper/hooks/useMetaTagsFormat';
import { useSelectSpecificSEOData } from '../../../components/SEOWrapper/hooks/useSelectSpecificSEOData';
import { useUpdateSeoTextLoading } from './hooks/useUpdateSeoTextLoading';
import { GAME } from '../../../constants/routeTemplates';
import { useIsDesktopLayout } from '../../../hooks/useMediaQuery';
import styles from './SEOWrapper.module.css';

const SEOWrapper = ({ children, containerClassName }) => {
  const isDesktop = useIsDesktopLayout();
  const isSSR = useIsSSRMode();
  const { path } = useRouteMatch();

  const translate = useTranslationMetaTags();
  const formatMetaTags = useMetaTagsFormat();
  const formatSEOText = useSEOTextFormat();

  const staticMetaTagsRequestUrl = useStaticMetaTagsRequestUrl();
  const metaTagsRequestURL = useMetaTagsRequestUrl();
  const SEOTextRequestURL = useSEOTextRequestUrl();
  const FAQRequestURL = useFAQRequestUrl();

  const selectSpecificSEOData = useSelectSpecificSEOData();
  const setIsSeoTextLoaded = useUpdateSeoTextLoading();

  const formatText = useCallback(
    (text) => {
      setIsSeoTextLoaded(Boolean(text));
      return formatSEOText(text);
    },
    [formatSEOText, setIsSeoTextLoaded]
  );

  return (
    <>
      <StaticMetaTags
        translate={translate}
        requestUrl={staticMetaTagsRequestUrl}
        format={formatMetaTags}
      />
      <MetaTags
        requestUrl={metaTagsRequestURL}
        translate={translate}
        queryOptions={{
          select: selectSpecificSEOData,
        }}
        format={formatMetaTags}
      />
      <CanonicalLink />
      <MetaRobots />
      {children}

      <div className={classNames(styles.container, containerClassName)}>
        <SEOWrapperBreadcrumbs
          className={classNames(styles.positioning, {
            [styles.breadcrumbs]: isDesktop && GAME === path,
          })}
        />

        <SEOText
          requestUrl={SEOTextRequestURL}
          collapsed={!isSSR}
          format={formatText}
          queryOptions={{
            select: selectSpecificSEOData,
          }}
          className={classNames(styles['seo-content'], styles.positioning)}
          iconClassName={styles.icon}
        />
        <FAQ
          requestUrl={FAQRequestURL}
          collapsed={!isSSR}
          format={formatSEOText}
          queryOptions={{
            select: selectSpecificSEOData,
          }}
          className={classNames(styles['seo-content'], styles.positioning)}
          iconClassName={styles.icon}
        />
        <div className={styles.backgroundLayer} />
      </div>

      <RenderPixel selectorId="seo-text" />
    </>
  );
};

export default withSEOContent(SEOWrapper);
